<script setup lang="ts">
import { computed, ref } from 'vue'
import { IInvoice } from '@/entityes/invoice/invoice.type'
import { IMenuItem, useInvoiceMenu } from '@/07-Shared/model/useInvoiceMenu'
import { isArray, isString } from 'lodash'
import usePolitic from '@/use/usePolitic'
import { useDeclarationRepository } from '@/entityes/invoice/declaration/declaration.repository'
import { useCfrRepository } from '@/entityes/invoice/cfr/cfr.repository'
import { useInvoiceRepository } from '@/entityes/invoice/invoice.repository'
import { useAsyncInvoiceReload } from '@/entityes/invoice/useAsyncInvoiceReload'
import { Refresh } from '@element-plus/icons-vue'
import { ElNotification } from 'element-plus'

interface IProps {
  invoice: IInvoice | undefined
  currentStep: string | undefined
  hasTms: boolean
  hasResolvedTms: boolean
}

const props = defineProps<IProps>()
const { menuTitles } = useInvoiceMenu()
const { isAdmin } = usePolitic()
const { switchDeclarationStep } = useDeclarationRepository()
const { switchCfrStep } = useCfrRepository()
const { getInvoice } = useInvoiceRepository()
const { initAsync } = useAsyncInvoiceReload()
const allSteps = computed(() =>
  menuTitles.filter((i) => i.serverTag).sort((item, item2) => item.sortIndex - item2.sortIndex)
)

const filteredSteps = computed(() => {
  return allSteps.value.filter((i: IMenuItem) => {
    const declarationSteps = props.invoice?.status.actions_for_goods?.Declaration
    const cfrSteps = props.invoice?.status.actions_for_goods?.Cfr

    if (isArray(i.serverTag)) {
      if (props.hasTms || props.hasResolvedTms) {
        return i
      }
    } else if (isString(i.serverTag)) {
      if (cfrSteps) {
        if (['Personal_data', 'Add_account'].includes(i.serverTag)) {
          return i
        }
      }
      if (declarationSteps) {
        if (i.serverTag !== 'Add_account') {
          return i
        }
      }
    }
    if (i.serverTag === 'invoice show') {
      return i
    }
  })
})
const currentStep = ref(props.currentStep)
const stepIndexMap = computed(() =>
  filteredSteps.value.reduce<Record<string, number>>((acc, step, index) => {
    acc[step.tag] = index
    return acc
  }, {})
)

const active = computed(() => {
  if (!props.currentStep) return 0
  if (props.currentStep === 'invoice show') {
    return 6
  } else {
    return stepIndexMap.value[props.currentStep] ?? 0
  }
})

function setStep(tag: string) {
  const availableSteps = filteredSteps.value.filter((i) => !['tms', 'personal_data'].includes(i.tag)).map((i) => i.tag)
  const body = { step: [filteredSteps.value.find((i) => i.tag === tag)?.serverTag] }
  const promise =
    tag === 'cfr'
      ? switchCfrStep(props.invoice?.goods.Cfr as number, body)
      : switchDeclarationStep(props.invoice?.goods.Declaration as number, body)

  if (!availableSteps.includes(tag)) {
    ElNotification({
      title: 'Ошибка',
      message: `Переключение доступно только с шага Дополнительные доходы`,
      type: 'error',
    })
  } else {
    promise.then(() => {
      getInvoice(props.invoice?.id as number)
        .then((invoice) => initAsync(invoice))
        .finally(() => {
          ElNotification({
            title: 'Успешно',
            message: `Отправлен запрос на изменение шага`,
            type: 'success',
          })
        })
    })
  }
}
</script>

<template>
  <div class="container-list">
    <el-steps :active="active" finish-status="success">
      <el-step v-for="item in filteredSteps" :title="item.title" :key="item.tag" />
    </el-steps>
    <el-button size="small" type="primary" v-if="isAdmin" @click="setStep(props.currentStep as string)" :icon="Refresh">
      Следующий
    </el-button>
  </div>
</template>

<style scoped lang="sass"></style>
